import { useState } from "react";
import { Order } from "@/redux/services/order/order.type";
import { MiniOrderCard } from "@/components/pos/terminal/PosActiveOrders";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import { CheckCircle2 } from "lucide-react";

export interface AttentionGroup {
  key: string;
  label: string;
  orders: Order[];
}

// The floor's working set: orders that need someone to do something now,
// grouped by what that action is (collect payment, serve, etc.). Tapping a
// card opens the shared order drawer.
const NeedsAttention = ({ groups }: { groups: AttentionGroup[] }) => {
  const [selected, setSelected] = useState<{
    id: string;
    orderNumber: string;
  } | null>(null);

  const nonEmpty = groups.filter((g) => g.orders.length > 0);

  if (nonEmpty.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border py-10 text-center">
        <CheckCircle2 className="h-8 w-8 text-green-500" />
        <p className="text-sm font-medium text-card-foreground">
          Tout est à jour
        </p>
        <p className="text-sm text-muted-foreground">
          Aucune commande à traiter pour le moment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {nonEmpty.map((group) => (
        <div key={group.key}>
          <div className="mb-3 flex items-center gap-2.5">
            <h2 className="text-base font-semibold text-foreground">
              {group.label}
            </h2>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
              {group.orders.length}
            </span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scroll-hidden">
            {group.orders.map((order) => (
              <MiniOrderCard
                key={order.id}
                order={order}
                onClick={() =>
                  setSelected({
                    id: order.id,
                    orderNumber: order.orderNumber,
                  })
                }
              />
            ))}
          </div>
        </div>
      ))}

      {selected && (
        <OrderDetailsDrawer
          orderId={selected.id}
          orderNumber={selected.orderNumber}
          isOpen
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default NeedsAttention;
